import { create } from 'zustand';
import { notificationsApi, NotificationTab } from '@/lib/api';
import { useAuthStore } from './auth-store';

export interface NotificationItem {
  id: number;
  message: string;
  redirection_url?: string | null;
  is_read: boolean | 0 | 1;
  is_mentioned?: boolean | 0 | 1;
  created_at: string;
}

export interface NotificationsState {
  activeTab: NotificationTab;
  items: NotificationItem[];
  page: number;
  limit: number;
  isLoading: boolean;
  error: string | null;
  total: number;
}

export interface NotificationsActions {
  setActiveTab: (tab: NotificationTab) => void;
  load: (reset?: boolean) => Promise<void>;
  loadMore: () => Promise<void>;
  markAsReadLocally: (id: number) => void;
}

export type NotificationsStore = NotificationsState & NotificationsActions;

export const useNotificationsStore = create<NotificationsStore>((set, get) => ({
  activeTab: 'all',
  items: [],
  page: 1,
  limit: 10,
  isLoading: false,
  error: null,
  total: 0,

  setActiveTab: (tab) => {
    set({ activeTab: tab, page: 1, items: [], total: 0 });
  },

  load: async (reset = false) => {
    const { activeTab, page, limit } = get();
    const { token } = useAuthStore.getState();
    if (!token) return;

    set({ isLoading: true, error: null });

    const resp = await notificationsApi.getNotifications(token, {
      tab: activeTab,
      page: reset ? 1 : page,
      limit,
    });

    if (!resp.success) {
      set({ isLoading: false, error: resp.message || 'Failed to load notifications' });
      return;
    }

    // Support data inside data or direct array with meta
    const data = resp.data;
    const list: NotificationItem[] = data?.data || data?.items || data || [];
    const total: number = data?.total || data?.meta?.total || list.length;

    set((state) => ({
      items: reset || page === 1 ? list : [...state.items, ...list],
      isLoading: false,
      total,
      page: reset ? 1 : state.page,
    }));
  },

  loadMore: async () => {
    const { isLoading, items, total } = get();
    if (isLoading) return;
    if (items.length >= total) return;
    set((state) => ({ page: state.page + 1 }));
    await get().load();
  },

  markAsReadLocally: (id) => {
    set((state) => ({
      items: state.items.map((n) => ({
        ...n,
        is_read: n.id === id ? 1 : n.is_read,
      })),
    }));
  },
}));
